(function () {
  'use strict';

  const cfg = window.SnoozePals || {};
  const routes = cfg.routes || {};
  const moneyFormat = cfg.moneyFormat || '${{amount}}';
  const gift = cfg.gift || {};
  const giftVariantIds = new Set((gift.variantIds || []).map(String));

  const overlay = document.getElementById('overlay');
  const drawer = document.getElementById('drawer');
  const menu = document.getElementById('mmenu');
  const cartItems = document.getElementById('cartItems');
  const cartCount = document.getElementById('cartCount');
  const subtotal = document.getElementById('subtotal');
  const freeShip = document.getElementById('freeship');
  const cartDiscounts = document.getElementById('cartDiscounts');
  const discountList = document.getElementById('discountList');
  const discountTotal = document.getElementById('discountTotal');
  const liveRegion = document.getElementById('cartLive');

  const progressEl = document.querySelector('[data-cart-progress]');
  const giftRow = document.querySelector('[data-gift-row]');
  const sockPicker = document.querySelector('[data-sock-picker]');
  const sockAddBtn = document.querySelector('[data-sock-add]');
  let selectedSock = null;
  const upsellWrap = document.querySelector('[data-cart-upsell]');
  const promoEl = document.querySelector('[data-cart-promo]');
  const PROMO_DISMISSED = 'sp_promo_dismissed';
  let promoShown = false;

  let lastFocused = null;
  let latestCart = null;

  /* ---------------------------------------------------------------- utils */

  function formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    const number = (decimals, thousands, decimal) => {
      const fixed = amount.toFixed(decimals);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return decimals ? parts[0] + decimal + parts[1] : parts[0];
    };
    const formats = {
      amount: number(2, ',', '.'),
      amount_no_decimals: number(0, ',', '.'),
      amount_with_comma_separator: number(2, '.', ','),
      amount_no_decimals_with_comma_separator: number(0, '.', ','),
      amount_with_space_separator: number(2, ' ', ','),
      amount_no_decimals_with_space_separator: number(0, ' ', ','),
      amount_with_apostrophe_separator: number(2, "'", '.')
    };
    return moneyFormat.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (match, key) => formats[key] || formats.amount);
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
  }

  function announce(message) {
    if (liveRegion) liveRegion.textContent = message;
  }

  function showToast(message) {
    const toast = document.getElementById('toast');
    const msg = document.getElementById('toastMsg');
    if (!toast || !msg) return;
    msg.textContent = message;
    toast.classList.add('show');
    clearTimeout(window.__snoozeToast);
    window.__snoozeToast = setTimeout(() => toast.classList.remove('show'), 2600);
  }

  /* ------------------------------------------------- focus trap / drawers */

  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function focusablesIn(root) {
    return Array.from(root.querySelectorAll(FOCUSABLE))
      .filter((el) => el.offsetParent !== null || el === document.activeElement);
  }

  function trapFocus(event) {
    const open = document.querySelector('.drawer.show, .mmenu.show');
    if (!open || event.key !== 'Tab') return;
    const items = focusablesIn(open);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openPanel(panel) {
    if (!panel || !overlay) return;
    lastFocused = document.activeElement;
    document.querySelectorAll('.drawer.show, .mmenu.show').forEach((el) => {
      el.classList.remove('show');
      el.setAttribute('aria-hidden', 'true');
    });
    panel.classList.add('show');
    panel.setAttribute('aria-hidden', 'false');
    overlay.classList.add('show');
    document.body.style.overflow = 'hidden';
    const first = focusablesIn(panel)[0];
    if (first) requestAnimationFrame(() => first.focus());
  }

  function closeAll() {
    let wasOpen = false;
    document.querySelectorAll('.drawer.show, .mmenu.show').forEach((el) => {
      wasOpen = true;
      el.classList.remove('show');
      el.setAttribute('aria-hidden', 'true');
    });
    if (overlay) overlay.classList.remove('show');
    document.body.style.overflow = '';
    if (wasOpen && lastFocused && document.contains(lastFocused)) lastFocused.focus();
    lastFocused = null;
  }

  function openCart() {
    openPanel(drawer);
    const body = drawer && drawer.querySelector('.drawer-body');
    if (body) body.scrollTop = 0;
    const expressPayments = drawer && drawer.querySelector('.cart-express-disclosure');
    if (expressPayments) expressPayments.open = false;
    refreshCart();
  }

  /* ------------------------------------------------------------ cart data */

  async function getCart() {
    const res = await fetch(routes.cartJson || '/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!res.ok) throw new Error('Cart unavailable');
    return res.json();
  }

  async function changeLine(line, quantity) {
    drawer && drawer.classList.add('cart-loading');
    try {
      const res = await fetch(routes.change || '/cart/change.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ line: String(line), quantity: String(quantity) })
      });
      if (!res.ok) throw new Error('Could not update cart');
      // Re-read the cart so discount allocations reflect the new state.
      renderCart(await getCart());
      announce(quantity === 0 ? 'Item removed from cart.' : 'Cart updated.');
    } catch (err) {
      showToast('Cart could not update. Please try again.');
      announce('Cart could not update.');
    } finally {
      drawer && drawer.classList.remove('cart-loading');
    }
  }

  async function addVariant(variantId, quantity) {
    const res = await fetch(routes.add || '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity || 1 }] })
    });
    if (!res.ok) {
      let message = 'Could not add that item.';
      try {
        const body = await res.json();
        if (body && body.description) message = body.description;
      } catch (e) { /* keep the default */ }
      throw new Error(message);
    }
    return res.json();
  }

  /* ------------------------------------------------------- qualifying total
     Mirrors the active promotion: merchandise only. Gift cards and the
     reward product itself never count toward the threshold, and taxes and
     shipping are not part of cart.items_subtotal_price to begin with. */

  function qualifyingTotal(cart) {
    return (cart.items || []).reduce((sum, item) => {
      if (item.gift_card) return sum;
      if (giftVariantIds.has(String(item.variant_id))) return sum;
      if (gift.productId && String(item.product_id) === String(gift.productId)) return sum;
      // final_line_price already includes any active discounts.
      return sum + Number(item.final_line_price || 0);
    }, 0);
  }

  /* -------------------------------------------------------------- render */

  function renderCart(cart) {
    latestCart = cart;
    if (!cartItems) return;

    const count = cart.item_count || 0;
    if (cartCount) {
      cartCount.textContent = count;
      cartCount.classList.toggle('show', count > 0);
    }

    if (!count) {
      cartItems.innerHTML =
        '<div class="cart-empty"><p class="em">Your cart is empty</p><p>Add a Snooze Pal and it will show up here.</p></div>';
    } else {
      cartItems.innerHTML = cart.items.map((item, index) => {
        // A line is only "the reward" when it is the gift product AND the cart
        // has actually discounted it to nothing. Otherwise it is a normal
        // purchase and must be shown, and charged, at its real price.
        const isGiftItem = giftVariantIds.has(String(item.variant_id));
        const isReward = isGiftItem && Number(item.final_line_price) === 0;
        const image = item.image
          ? `<img class="thumb" src="${escapeHtml(item.image)}" alt="" loading="lazy" width="76" height="76">`
          : '<div class="thumb"></div>';
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';

        // Real per-line discounts, named exactly as the cart returns them.
        const allocs = item.line_level_discount_allocations || [];
        const lineDiscount = allocs
          .filter((a) => Number(a.amount) > 0)
          .map((a) => {
            const title = (a.discount_application && a.discount_application.title) || 'Discount';
            return `<span class="line-discount">${escapeHtml(title)} &minus;${formatMoney(a.amount)}</span>`;
          }).join('');

        const wasPrice = item.original_line_price > item.final_line_price
          ? `<s>${formatMoney(item.original_line_price)}</s>` : '';

        return `<div class="citem${isReward ? ' is-reward' : ''}" data-line="${index + 1}">
          ${image}
          <div class="info">
            <b>${escapeHtml(item.product_title)}</b>
            ${variant ? `<div class="meta">${escapeHtml(variant)}</div>` : '<div class="meta"></div>'}
            <div class="cart-qty">
              <button type="button" data-cart-minus aria-label="Decrease quantity of ${escapeHtml(item.product_title)}">&minus;</button>
              <span aria-hidden="true">${item.quantity}</span>
              <button type="button" data-cart-plus aria-label="Increase quantity of ${escapeHtml(item.product_title)}">+</button>
            </div>
            ${lineDiscount}
            <button class="rm" type="button" data-cart-remove>Remove<span class="visually-hidden"> ${escapeHtml(item.product_title)}</span></button>
          </div>
          <div class="pr">${formatMoney(item.final_line_price)}${wasPrice}</div>
        </div>`;
      }).join('');
    }

    renderShipping(cart);
    renderDiscounts(cart);
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
    renderProgress(cart);
    renderGift(cart);
    renderUpsell(cart);
    maybeShowPromo(cart);
  }

  function renderShipping(cart) {
    if (!freeShip) return;
    const goal = Number(cfg.freeShipThreshold || 0);
    // The milestone bar already tracks free shipping; don't say it twice.
    if (goal <= 0 || progressEl) { freeShip.style.display = 'none'; return; }
    freeShip.style.display = '';
    const total = qualifyingTotal(cart);
    freeShip.innerHTML = total >= goal
      ? "You've earned <b>free shipping</b>"
      : `Spend ${formatMoney(goal - total)} more for <b>free shipping</b>`;
  }

  function renderDiscounts(cart) {
    if (!cartDiscounts || !discountList || !discountTotal) return;
    const rows = [];
    (cart.cart_level_discount_applications || []).forEach((d) => {
      const amt = Number(d.total_allocated_amount || 0);
      if (amt > 0) rows.push({ title: d.title || d.key || 'Discount', amount: amt });
    });
    (cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((d) => {
        const amt = Number(d.amount || 0);
        const title = (d.discount_application && d.discount_application.title) || 'Discount';
        if (amt > 0) rows.push({ title, amount: amt });
      });
    });
    const merged = {};
    rows.forEach((r) => { merged[r.title] = (merged[r.title] || 0) + r.amount; });
    const titles = Object.keys(merged);
    const savings = Number(cart.total_discount || 0);

    if (titles.length && savings > 0) {
      discountList.innerHTML = titles.map((t) =>
        `<div class="cart-discount-row"><span>${escapeHtml(t)}</span><span>&minus;${formatMoney(merged[t])}</span></div>`
      ).join('');
      discountTotal.textContent = '−' + formatMoney(savings);
      cartDiscounts.style.display = 'block';
    } else if (savings > 0) {
      discountList.innerHTML =
        `<div class="cart-discount-row"><span>Discount applied</span><span>&minus;${formatMoney(savings)}</span></div>`;
      discountTotal.textContent = '−' + formatMoney(savings);
      cartDiscounts.style.display = 'block';
    } else {
      cartDiscounts.style.display = 'none';
    }
  }

  /* The reward.
     Progress is display only. Whether the socks are free is decided by the
     active discount, and we render whatever price the cart returns. No branch
     here is allowed to print a zero the cart did not give us. */
  /* Milestone progress.
     Draws whichever rewards the merchant configured — free shipping, the free
     gift, or both — against the cart's qualifying total, and names the next one
     still to be reached. Nothing here decides what anything costs. */
  function renderProgress(cart) {
    if (!progressEl) return;

    const giftName = progressEl.dataset.giftName || gift.name || 'your reward';

    // Milestones come from the rendered list, so adding a reward in theme
    // settings needs no change here — however many <li>s exist, all are tracked.
    const markEls = Array.from(progressEl.querySelectorAll('[data-mark]'));
    const marks = markEls
      .map((el) => ({
        el,
        at: Number(el.dataset.mark || 0),
        label: (el.querySelector('.cart-mark-label')?.childNodes[0]?.textContent || '').trim().toLowerCase()
      }))
      .filter((m) => m.at > 0);
    if (!marks.length) { progressEl.hidden = true; return; }
    marks.sort((a, b) => a.at - b.at);

    const total = qualifyingTotal(cart);
    const ceiling = marks[marks.length - 1].at;
    const pct = ceiling > 0 ? Math.min(100, (total / ceiling) * 100) : 0;
    const next = marks.find((m) => total < m.at);

    // If the gift is in the cart but the discount has not applied, say so.
    const giftLine = (cart.items || []).find((i) => giftVariantIds.has(String(i.variant_id)));
    const chargedAnyway = Boolean(giftLine) && Number(giftLine.final_line_price) > 0;
    const discountConfirmed = Boolean(giftLine) && Number(giftLine.final_line_price) === 0;

    const text = progressEl.querySelector('[data-progress-text]');
    if (text) {
      if (chargedAnyway) {
        text.textContent = `${giftName} added. The reward discount is not applying yet.`;
      } else if (next) {
        text.innerHTML = `You're <b>${formatMoney(next.at - total)}</b> away from ${escapeHtml(next.label)}.`;
      } else if (discountConfirmed) {
        text.textContent = `Your free ${giftName} are confirmed in the cart.`;
      } else {
        text.textContent = `You've reached ${marks.map((m) => m.label).join(' and ')}. Your available rewards are ready.`;
      }
    }

    const fill = progressEl.querySelector('[data-progress-fill]');
    const knob = progressEl.querySelector('[data-progress-knob]');
    const bar = progressEl.querySelector('[data-progress-bar]');
    if (fill) fill.style.width = pct + '%';
    if (knob) knob.style.left = pct + '%';
    if (bar) bar.setAttribute('aria-valuenow', String(Math.round(pct)));

    marks.forEach((m) => {
      m.el.style.left = ceiling > 0 ? (m.at / ceiling) * 100 + '%' : '100%';
      m.el.classList.toggle('reached', total >= m.at);
    });

    progressEl.classList.toggle('all-unlocked', !next);
    progressEl.classList.toggle('is-warning', chargedAnyway);
  }

  function renderGift(cart) {
    if (!giftRow && !sockPicker) return;

    const goal = Number(gift.threshold || 0);
    const name = gift.name || 'your free gift';
    const total = qualifyingTotal(cart);
    const unlocked = goal > 0 && total >= goal;

    // Use the cart's actual gift line, if the gift is in the cart at all.
    const giftLine = (cart.items || []).find((i) => giftVariantIds.has(String(i.variant_id)));
    const inCart = Boolean(giftLine);

    if (giftRow) {
      // Hidden once the socks are in the cart — the line item speaks for itself.
      giftRow.hidden = unlocked || inCart;
      const sub = giftRow.querySelector('[data-gift-row-sub]');
      if (sub && !unlocked && goal > 0) {
        sub.textContent = `Spend ${formatMoney(Math.max(0, goal - total))} more to unlock`;
      }
    }

    if (sockPicker) {
      sockPicker.hidden = !unlocked || inCart;
      if (sockPicker.hidden) clearSockSelection();
    }
  }

  /* Upsell popup.
     Offers the products the merchant picked. It is advisory only: it never
     covers the checkout button permanently, never blocks a click, and once
     dismissed it stays dismissed for the rest of the session. */
  function promoDismissed() {
    try { return sessionStorage.getItem(PROMO_DISMISSED) === '1'; }
    catch (e) { return promoShown === 'dismissed'; }
  }

  function dismissPromo() {
    if (!promoEl) return;
    promoEl.classList.remove('show');
    promoEl.hidden = true;
    promoEl.setAttribute('aria-hidden', 'true');
    promoShown = 'dismissed';
    try { sessionStorage.setItem(PROMO_DISMISSED, '1'); } catch (e) { /* private mode */ }
  }

  function showPromo() {
    if (!promoEl || promoDismissed()) return;
    promoEl.hidden = false;
    promoEl.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => promoEl.classList.add('show'));
    promoShown = true;
  }

  function maybeShowPromo(cart) {
    if (!promoEl || promoShown === true || promoDismissed()) return;
    // Nothing to upsell against an empty cart.
    if (!cart || !(cart.item_count > 0)) return;

    // Hide any product already in the cart, and bail if that empties the panel.
    const inCart = new Set((cart.items || []).map((i) => String(i.product_id)));
    let remaining = 0;
    promoEl.querySelectorAll('[data-promo-item]').forEach((el) => {
      const hide = inCart.has(String(el.dataset.productId));
      el.hidden = hide;
      if (!hide) remaining += 1;
    });
    if (!remaining) { promoEl.hidden = true; return; }

    const trigger = (cfg.promoTrigger || 'open');
    if (trigger === 'open') {
      clearTimeout(window.__snoozePromo);
      window.__snoozePromo = setTimeout(showPromo, 900);
    }
    // The 'checkout' trigger is wired to the checkout button below.
  }

  function clearSockSelection() {
    selectedSock = null;
    if (!sockPicker) return;
    sockPicker.querySelectorAll('[data-sock-variant]').forEach((b) => b.setAttribute('aria-checked', 'false'));
    if (sockAddBtn) {
      sockAddBtn.disabled = true;
      const label = sockAddBtn.querySelector('[data-sock-add-label]');
      if (label) label.textContent = 'Choose a color first';
    }
  }

  function renderUpsell(cart) {
    if (!upsellWrap) return;
    const cards = Array.from(upsellWrap.querySelectorAll('[data-upsell-card]'));
    if (!cards.length) return;
    const inCart = new Set((cart.items || []).map((i) => String(i.variant_id)));
    const limit = Math.max(1, Number(upsellWrap.dataset.upsellLimit || 2));
    let missing = 0;
    let remaining = 0;
    let visible = 0;
    cards.forEach((card) => {
      const owned = inCart.has(String(card.dataset.variantId));
      if (owned) { card.hidden = true; return; }
      missing += 1;
      if (card.dataset.unavailable === 'true') { card.hidden = true; return; }
      remaining += 1;
      const show = visible < limit;
      card.hidden = !show;
      if (show) visible += 1;
    });
    const empty = upsellWrap.querySelector('[data-upsell-empty]');
    if (empty) {
      empty.hidden = remaining > 0;
      if (!remaining) {
        empty.textContent = missing > 0
          ? 'The remaining pals are currently sold out.'
          : "You've got the whole set.";
      }
    }
  }

  async function refreshCart() {
    try {
      renderCart(await getCart());
    } catch (err) {
      showToast('Cart could not refresh. Please try again.');
    }
  }

  /* ------------------------------------------------------------- handlers */

  // AJAX add for every product form in the theme.
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    const message = form.querySelector('[data-form-message]');
    if (button) button.disabled = true;
    try {
      const res = await fetch(routes.add || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      if (!res.ok) throw new Error('Add failed');
      const quickDialog = form.closest('[data-bundle-quick-dialog]');
      if (quickDialog && quickDialog.open) quickDialog.close();
      if (message) {
        message.textContent = 'Added to cart';
        message.classList.add('show');
        clearTimeout(message.__hideTimer);
        message.__hideTimer = setTimeout(() => message.classList.remove('show'), 3000);
      }
      showToast('Added to cart');
      announce('Added to cart.');
      await refreshCart();
      openCart();
    } catch (err) {
      showToast('Could not add that item.');
      announce('Could not add that item.');
    } finally {
      if (button) button.disabled = false;
    }
  });

  // Bundle cards use an on-page chooser so shoppers do not have to visit a
  // second product page just to select a combination.
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-bundle-quick-open]');
    if (opener) {
      event.preventDefault();
      const dialog = document.getElementById(opener.getAttribute('aria-controls'));
      if (dialog && typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        const detailsLink = dialog && dialog.querySelector('.bundle-quick-details');
        if (detailsLink) window.location.href = detailsLink.href;
      }
      return;
    }

    const closer = event.target.closest('[data-bundle-quick-close]');
    if (closer) {
      event.preventDefault();
      closer.closest('[data-bundle-quick-dialog]')?.close();
      return;
    }

    const dialog = event.target.closest('[data-bundle-quick-dialog]');
    if (dialog && event.target === dialog) dialog.close();
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-bundle-variant-select]');
    if (!select) return;
    const option = select.options[select.selectedIndex];
    const dialog = select.closest('[data-bundle-quick-dialog]');
    const image = dialog && dialog.querySelector('[data-bundle-quick-image]');
    const src = option && option.dataset.imageSrc;
    if (!image || !src) return;
    const nextSrc = new URL(src, document.baseURI).href;
    if (image.src === nextSrc) {
      image.alt = option.dataset.imageAlt || '';
      return;
    }

    image.classList.add('is-changing');
    image.onload = () => image.classList.remove('is-changing');
    image.onerror = () => image.classList.remove('is-changing');
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = src;
    image.alt = option.dataset.imageAlt || '';
  });

  // Choosing a colour only selects it. Adding is a deliberate second step.
  if (sockPicker) {
    sockPicker.addEventListener('click', (event) => {
      const swatch = event.target.closest('[data-sock-variant]');
      if (!swatch || swatch.disabled) return;
      sockPicker.querySelectorAll('[data-sock-variant]').forEach((b) => b.setAttribute('aria-checked', 'false'));
      swatch.setAttribute('aria-checked', 'true');
      selectedSock = { id: swatch.dataset.sockVariant, title: swatch.dataset.sockTitle || '' };
      if (sockAddBtn) {
        sockAddBtn.disabled = false;
        const label = sockAddBtn.querySelector('[data-sock-add-label]');
        if (label) label.textContent = `Add ${selectedSock.title}`;
      }
      const note = sockPicker.querySelector('[data-sock-note]');
      if (note) note.textContent = '';
    });
  }

  // Add the exact selected variant, then re-read the cart and report only what
  // returned by the cart — the price and the discount allocation.
  if (sockAddBtn) {
    sockAddBtn.addEventListener('click', async () => {
      if (!selectedSock) return;
      const selection = { ...selectedSock };
      const note = sockPicker.querySelector('[data-sock-note]');
      sockAddBtn.disabled = true;
      sockAddBtn.setAttribute('aria-busy', 'true');
      try {
        await addVariant(selection.id, 1);
        const cart = await getCart();
        const line = (cart.items || []).find((i) => String(i.variant_id) === String(selection.id));
        let resultMessage = `${selection.title} socks added to your cart.`;
        let toastMessage = 'Socks added';
        if (line) {
          const paid = Number(line.final_line_price);
          const allocs = line.line_level_discount_allocations || [];
          const title = allocs.length && allocs[0].discount_application
            ? allocs[0].discount_application.title : null;
          if (paid === 0) {
            resultMessage = title
              ? `Added free of charge (${title}).`
              : 'Added free of charge.';
            toastMessage = `Free ${gift.name || 'socks'} added`;
          } else {
            resultMessage = `Added at ${formatMoney(paid)}. The reward discount is not active, so this item is not free.`;
            toastMessage = 'Reward discount not applied';
          }
        }
        renderCart(cart);
        announce(resultMessage);
        showToast(toastMessage);
      } catch (err) {
        if (note) note.textContent = err.message;
        showToast(err.message);
        announce(err.message);
        sockAddBtn.disabled = false;
      } finally {
        sockAddBtn.removeAttribute('aria-busy');
      }
    });
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-promo-dismiss]')) { event.preventDefault(); dismissPromo(); return; }
    if (event.target.closest('#cartBtn')) { event.preventDefault(); openCart(); return; }
    if (event.target.closest('#burger')) { event.preventDefault(); openPanel(menu); return; }
    if (event.target.closest('#closeCart') || event.target.closest('#closeMenu') || event.target === overlay) {
      closeAll();
      return;
    }
    const accordion = event.target.closest('.acc-head');
    if (accordion) {
      const acc = accordion.closest('.acc');
      const open = acc.classList.toggle('open');
      accordion.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (promoEl && promoEl.classList.contains('show')) { event.preventDefault(); dismissPromo(); return; }
      const openPanelEl = document.querySelector('.drawer.show, .mmenu.show');
      if (openPanelEl) { event.preventDefault(); closeAll(); }
    }
    trapFocus(event);
  });

  if (promoEl && (cfg.promoTrigger || 'open') === 'checkout') {
    const checkoutBtn = document.querySelector('.cart-checkout');
    if (checkoutBtn) {
      const reach = () => { if (latestCart && latestCart.item_count > 0) showPromo(); };
      checkoutBtn.addEventListener('pointerenter', reach);
      checkoutBtn.addEventListener('focus', reach);
    }
  }

  if (cartItems) {
    cartItems.addEventListener('click', (event) => {
      const row = event.target.closest('[data-line]');
      if (!row) return;
      const line = Number(row.dataset.line);
      const qty = Number(row.querySelector('.cart-qty span').textContent || 1);
      if (event.target.closest('[data-cart-minus]')) changeLine(line, Math.max(0, qty - 1));
      if (event.target.closest('[data-cart-plus]')) changeLine(line, qty + 1);
      if (event.target.closest('[data-cart-remove]')) changeLine(line, 0);
    });
  }

  // Accordion buttons get correct initial ARIA.
  document.querySelectorAll('.acc').forEach((acc) => {
    const head = acc.querySelector('.acc-head');
    if (head) head.setAttribute('aria-expanded', acc.classList.contains('open') ? 'true' : 'false');
  });

  addEventListener('scroll', () => {
    const header = document.getElementById('header');
    if (header) header.classList.toggle('scrolled', scrollY > 8);
  }, { passive: true });

  const productJson = document.querySelector('[data-product-json]');
  if (productJson) {
    const variants = JSON.parse(productJson.textContent);
    const selected = {};
    const priceEl = document.querySelector('[data-product-price]');
    const priceWrap = priceEl ? priceEl.parentElement : null;
    const updateVariantPrice = (variant) => {
      if (!priceEl || !variant) return;
      priceEl.textContent = formatMoney(variant.price);
      if (priceWrap) {
        const oldWas = priceWrap.querySelector('.was');
        const oldOff = priceWrap.querySelector('.off');
        if (oldWas) oldWas.remove();
        if (oldOff) oldOff.remove();
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const pct = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
          const was = document.createElement('span');
          was.className = 'was';
          was.textContent = formatMoney(variant.compare_at_price);
          const off = document.createElement('span');
          off.className = 'off';
          off.textContent = 'Save ' + pct + '%';
          priceWrap.appendChild(was);
          priceWrap.appendChild(off);
        }
      }
      const addBtn = document.getElementById('pAdd');
      if (addBtn) {
        if (variant.available) { addBtn.disabled = false; addBtn.textContent = 'Add to Cart'; }
        else { addBtn.disabled = true; addBtn.textContent = 'Sold Out'; }
      }
    };
    /* Swap an <img> to a new source.
       Setting src alone is not enough when the element also carries srcset —
       the browser keeps honouring the candidate list and the picture never
       changes. Clearing srcset/sizes first is what makes the swap stick.
       This helper was being called but had gone missing, which threw a
       ReferenceError and killed every thumbnail and arrow click. */
    const forceImageSource = (img, src) => {
      if (!img || !src) return;
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = src;
    };

    const mainProductImage = document.querySelector('[data-main-product-image]');
    const mobileSlides = Array.from(document.querySelectorAll('.mobile-media-slide[data-image-src]'));
    const setMobileVariantImage = (src) => {
      if (!src || !mobileSlides.length) return;
      const mobileGallery = mobileSlides[0].closest('.mobile-gallery');
      if (!mobileGallery || getComputedStyle(mobileGallery).display === 'none') return;
      const firstSlide = mobileSlides[0];
      const firstImage = firstSlide.querySelector('img');
      if (firstImage) {
        forceImageSource(firstImage, src);
        firstSlide.dataset.imageSrc = src;
      }
      mobileGallery.scrollTo({ left: 0, behavior: 'smooth' });
    };
    const setMainImage = (src, mediaId, forceMobileFirst = false) => {
      if (!mainProductImage || !src) return;
      forceImageSource(mainProductImage, src);
      if (forceMobileFirst) {
        setMobileVariantImage(src);
        return;
      }
      const targetSlide = mobileSlides.find((slide) => mediaId && slide.dataset.mediaId === String(mediaId))
        || mobileSlides.find((slide) => slide.dataset.imageSrc === src);
      const mobileGallery = targetSlide?.closest('.mobile-gallery');
      if (targetSlide && mobileGallery && getComputedStyle(mobileGallery).display !== 'none') {
        targetSlide.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      } else if (mobileSlides[0]) {
        setMobileVariantImage(src);
      }
    };
    document.querySelectorAll('[data-product-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => setMainImage(thumb.dataset.imageSrc, thumb.dataset.mediaId));
    });
    // Gallery arrow navigation
    const galleryThumbs = Array.from(document.querySelectorAll('.thumbs [data-product-thumb]'));
    let galleryIndex = 0;
    const goToGalleryIndex = (i) => {
      if (!galleryThumbs.length) return;
      galleryIndex = (i + galleryThumbs.length) % galleryThumbs.length;
      const t = galleryThumbs[galleryIndex];
      setMainImage(t.dataset.imageSrc, t.dataset.mediaId);
      t.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
      galleryThumbs.forEach((el, n) => {
        el.classList.toggle('is-active', n === galleryIndex);
        el.setAttribute('aria-current', n === galleryIndex ? 'true' : 'false');
      });
    };
    const markThumb = (i) => {
      galleryThumbs.forEach((t, n) => {
        t.classList.toggle('is-active', n === i);
        t.setAttribute('aria-current', n === i ? 'true' : 'false');
      });
      const dots = document.querySelector('[data-mobile-dots]');
      if (dots) dots.querySelectorAll('i').forEach((d, n) => d.classList.toggle('on', n === i));
    };
    markThumb(0);

    document.querySelector('[data-gallery-prev]')?.addEventListener('click', () => goToGalleryIndex(galleryIndex - 1));
    document.querySelector('[data-gallery-next]')?.addEventListener('click', () => goToGalleryIndex(galleryIndex + 1));
    galleryThumbs.forEach((t, i) => t.addEventListener('click', () => { galleryIndex = i; markThumb(i); }));

    // Left/right arrows move the gallery whenever focus is inside it.
    const galleryRoot = document.querySelector('.desktop-gallery');
    galleryRoot?.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      goToGalleryIndex(galleryIndex + (e.key === 'ArrowRight' ? 1 : -1));
      markThumb(galleryIndex);
    });

    // Dots are real controls on mobile, not decoration.
    const dotWrap = document.querySelector('[data-mobile-dots]');
    if (dotWrap && mobileSlides.length) {
      dotWrap.removeAttribute('aria-hidden');
      dotWrap.querySelectorAll('i').forEach((dot, i) => {
        dot.setAttribute('role', 'button');
        dot.setAttribute('tabindex', '0');
        dot.setAttribute('aria-label', `Go to image ${i + 1}`);
        const go = () => {
          mobileSlides[i]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          markThumb(i);
        };
        dot.addEventListener('click', go);
        dot.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } });
      });
    }
    document.querySelectorAll('[data-option-position]').forEach((button) => {
      if (button.classList.contains('on')) selected[button.dataset.optionPosition] = button.dataset.optionValue;
      button.addEventListener('click', () => {
        const pos = button.dataset.optionPosition;
        selected[pos] = button.dataset.optionValue;
        document.querySelectorAll(`[data-option-position="${pos}"]`).forEach((b) => b.classList.remove('on'));
        button.classList.add('on');
        const label = document.querySelector(`[data-option-label="${pos}"]`);
        if (label) label.textContent = button.dataset.optionValue;
        const variant = variants.find((v) => v.options.every((value, index) => selected[String(index + 1)] === value));
        const input = document.querySelector('[data-variant-id]');
        if (variant && input) input.value = variant.id;
        if (variant) updateVariantPrice(variant);
        // Only change the gallery image when THIS swatch carries its own image
        // (color swatches). Size/other options must not pull a fallback image.
        const ownImage = button.dataset.optionImage;
        if (ownImage) {
          setMainImage(ownImage, button.dataset.optionMediaId, true);
        }
      });
    });
    const qtyVal = document.querySelector('[data-qty-val]');
    const qtyInput = document.querySelector('[data-qty-input]');
    let qty = 1;
    const setQty = (next) => {
      qty = Math.max(1, next);
      if (qtyVal) qtyVal.textContent = qty;
      if (qtyInput) qtyInput.value = qty;
    };
    document.querySelector('[data-qty-minus]')?.addEventListener('click', () => setQty(qty - 1));
    document.querySelector('[data-qty-plus]')?.addEventListener('click', () => setQty(qty + 1));
  }

  const searchPanel = document.getElementById('searchPanel');
  document.getElementById('searchBtn')?.addEventListener('click', () => searchPanel?.classList.add('show'));
  document.getElementById('closeSearch')?.addEventListener('click', () => searchPanel?.classList.remove('show'));

  const popup = document.getElementById('popup');
  const closePopup = () => popup?.classList.remove('show');
  document.getElementById('closePop')?.addEventListener('click', closePopup);
  document.getElementById('skipPop')?.addEventListener('click', closePopup);
  popup?.addEventListener('click', (event) => { if (event.target === popup) closePopup(); });
  if (popup && !sessionStorage.getItem('snoozePopupSeen')) {
    setTimeout(() => {
      popup.classList.add('show');
      sessionStorage.setItem('snoozePopupSeen', '1');
    }, 3400);
  }

  addEventListener('scroll', () => document.getElementById('header')?.classList.toggle('scrolled', scrollY > 8));
  document.querySelectorAll('[data-review-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-carousel-track]');
    const slides = Array.from(carousel.querySelectorAll('[data-carousel-slide]'));
    const dots = carousel.querySelector('[data-carousel-dots]');
    const prev = carousel.querySelector('[data-carousel-prev]');
    const next = carousel.querySelector('[data-carousel-next]');
    if (!track || slides.length < 2) {
      prev?.setAttribute('hidden', '');
      next?.setAttribute('hidden', '');
      return;
    }
    let index = 0;
    const slideLeft = (slide) => slide.offsetLeft - track.offsetLeft;
    const setActiveDot = () => {
      dots?.querySelectorAll('button').forEach((dot, i) => dot.classList.toggle('active', i === index));
    };
    const goTo = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      track.scrollTo({ left: slideLeft(slides[index]), behavior: 'smooth' });
      setActiveDot();
    };
    if (dots) {
      dots.innerHTML = slides.map((_, i) => `<button type="button" aria-label="Go to review ${i + 1}"></button>`).join('');
      dots.querySelectorAll('button').forEach((dot, i) => dot.addEventListener('click', () => goTo(i)));
    }
    prev?.addEventListener('click', () => goTo(index - 1));
    next?.addEventListener('click', () => goTo(index + 1));
    track.addEventListener('scroll', () => {
      const closest = slides.reduce((best, slide, i) => {
        const distance = Math.abs(slideLeft(slide) - track.scrollLeft);
        return distance < best.distance ? { i, distance } : best;
      }, { i: index, distance: Infinity });
      if (closest.i !== index) {
        index = closest.i;
        setActiveDot();
      }
    }, { passive: true });
    addEventListener('resize', () => goTo(index));
    goTo(0);
  });
  // Thank-you modals (contact + newsletter) - auto-open when present, then close handlers
  document.querySelectorAll('.ty-modal').forEach((modal) => {
    requestAnimationFrame(() => modal.classList.add('show'));
    const close = () => modal.classList.remove('show');
    modal.querySelectorAll('[data-ty-close]').forEach((b) => b.addEventListener('click', close));
    modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  });
  // Image zoom lightbox (mobile pinch + double-tap, desktop click)
  (function initZoom() {
    const lightbox = document.getElementById('zoomLightbox');
    const zoomImg = document.getElementById('zoomImg');
    if (!lightbox || !zoomImg) return;
    let scale = 1, tx = 0, ty = 0, startDist = 0, startScale = 1;
    let panning = false, startX = 0, startY = 0, lastTap = 0;

    const apply = () => { zoomImg.style.transform = `translate(${tx}px,${ty}px) scale(${scale})`; };
    const reset = () => { scale = 1; tx = 0; ty = 0; apply(); };

    const open = (src) => {
      zoomImg.src = src;
      reset();
      lightbox.classList.add('show');
      document.body.style.overflow = 'hidden';
    };
    const close = () => {
      lightbox.classList.remove('show');
      document.body.style.overflow = '';
    };

    // Open triggers: any [data-zoomable] image or the zoom-hint button
    document.addEventListener('click', (e) => {
      const hint = e.target.closest('[data-zoom-open]');
      const zoomable = e.target.closest('[data-zoomable]');
      if (hint) {
        const img = document.querySelector('[data-main-product-image]');
        if (img) open(img.dataset.zoomSrc || img.src);
        return;
      }
      if (zoomable) {
        // Mobile slide: only open zoom (don't block thumb-switch on desktop thumbs)
        const src = zoomable.dataset.zoomSrc || (zoomable.querySelector('img') && zoomable.querySelector('img').src) || zoomable.src;
        if (src && (zoomable.tagName === 'IMG' || zoomable.classList.contains('mobile-media-slide'))) open(src);
      }
    });

    lightbox.querySelectorAll('[data-zoom-close]').forEach((b) => b.addEventListener('click', close));
    lightbox.addEventListener('click', (e) => { if (e.target === lightbox || e.target.classList.contains('zoom-stage')) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && lightbox.classList.contains('show')) close(); });

    // Pinch zoom
    zoomImg.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        startDist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
        startScale = scale;
      } else if (e.touches.length === 1 && scale > 1) {
        panning = true;
        startX = e.touches[0].clientX - tx;
        startY = e.touches[0].clientY - ty;
      }
    }, { passive: true });

    zoomImg.addEventListener('touchmove', (e) => {
      if (e.touches.length === 2) {
        const dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
        scale = Math.min(4, Math.max(1, startScale * (dist / startDist)));
        apply();
      } else if (panning && e.touches.length === 1) {
        tx = e.touches[0].clientX - startX;
        ty = e.touches[0].clientY - startY;
        apply();
      }
    }, { passive: true });

    zoomImg.addEventListener('touchend', (e) => {
      panning = false;
      if (scale <= 1) reset();
      // Double-tap to toggle zoom
      const now = Date.now();
      if (now - lastTap < 300 && e.changedTouches.length) {
        scale = scale > 1 ? 1 : 2.5;
        tx = 0; ty = 0; apply();
      }
      lastTap = now;
    });

    // Desktop double-click toggle
    zoomImg.addEventListener('dblclick', () => { scale = scale > 1 ? 1 : 2.2; tx = 0; ty = 0; apply(); });
  })();

  /* ------------------------------------------- sticky mobile add-to-cart */
  (function stickyAtc() {
    const bar = document.querySelector('[data-sticky-atc]');
    const form = document.getElementById('productForm');
    const mainAdd = document.getElementById('pAdd');
    if (!bar || !form || !mainAdd) return;

    const addBtn = bar.querySelector('[data-sticky-add]');
    const priceEl = bar.querySelector('[data-sticky-price]');

    // Mirror the real button's state rather than keeping a second source of truth.
    const sync = () => {
      addBtn.disabled = mainAdd.disabled;
      addBtn.textContent = mainAdd.textContent.trim();
      const live = document.querySelector('[data-product-price]');
      if (priceEl && live) priceEl.textContent = live.textContent;
    };
    new MutationObserver(sync).observe(mainAdd, {
      attributes: true, childList: true, characterData: true, subtree: true
    });
    const livePrice = document.querySelector('[data-product-price]');
    if (livePrice) new MutationObserver(sync).observe(livePrice, { childList: true, characterData: true, subtree: true });
    sync();

    addBtn.addEventListener('click', () => {
      form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    });

    // Show it once the real button has scrolled out of view.
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const show = !entry.isIntersecting;
        bar.classList.toggle('show', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      });
    }, { rootMargin: '0px 0px -40px 0px' });
    io.observe(mainAdd);
  })();

  /* ------------------------------------------------ mobile gallery dots */
  (function galleryDots() {
    const gallery = document.querySelector('[data-mobile-gallery]');
    const dots = document.querySelector('[data-mobile-dots]');
    if (!gallery || !dots) return;
    const slides = Array.from(gallery.children);
    const marks = Array.from(dots.children);
    gallery.addEventListener('scroll', () => {
      const centre = gallery.scrollLeft + gallery.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      slides.forEach((slide, i) => {
        const mid = slide.offsetLeft + slide.offsetWidth / 2 - gallery.offsetLeft;
        const dist = Math.abs(mid - centre);
        if (dist < bestDist) { bestDist = dist; best = i; }
      });
      marks.forEach((m, i) => m.classList.toggle('on', i === best));
    }, { passive: true });
  })();

  refreshCart();
})();
